/**
 * SnippetWizard - Multi-step wizard for creating snippet media items
 *
 * Steps:
 * 1. Image Preparation (rotate, crop, enhance)
 * 2. Classification (document type, text type, record subtype)
 * 3. Metadata (caption, description, date, location)
 * 4. Review and Save
 */

import { useState, useCallback, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SnippetItem,
  SnippetDraftData,
  createInitialDraftData,
  finalizeSnippet,
  deleteSnippet,
  FinalizeData,
} from "../../api/snippets";
import { useSnippetDraft } from "../../hooks/useSnippetDraft";
import StepImagePrep from "./steps/StepImagePrep";
import StepClassification from "./steps/StepClassification";
import StepMetadata from "./steps/StepMetadata";
import StepReview from "./steps/StepReview";

// =============================================================================
// Types
// =============================================================================

interface SnippetWizardProps {
  snippet: SnippetItem;
  treeId?: number;
  onClose: () => void;
  onComplete: () => void;
}

type WizardStep = 1 | 2 | 3 | 4;

const STEP_TITLES: Record<WizardStep, string> = {
  1: "Prepare Image",
  2: "Classify",
  3: "Add Details",
  4: "Review & Save",
};

// =============================================================================
// Component
// =============================================================================

export function SnippetWizard({ snippet, treeId, onClose, onComplete }: SnippetWizardProps) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Draft state management
  const initialDraft = snippet.draft_data || createInitialDraftData();
  const [draftData, setDraftData] = useState<SnippetDraftData>(initialDraft);
  const [currentStep, setCurrentStep] = useState<WizardStep>(
    (initialDraft.current_step as WizardStep) || 1
  );

  // Auto-save hook
  const { isSaving, lastSaved, saveError } = useSnippetDraft(snippet.id, draftData);

  // Track if user has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // UI state
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track image refresh key (for after rotate/crop/enhance)
  const [imageKey, setImageKey] = useState(0);

  // Update draft data when step changes
  useEffect(() => {
    setDraftData((prev) => ({
      ...prev,
      current_step: currentStep,
    }));
  }, [currentStep]);

  // Finalize mutation
  const finalizeMutation = useMutation({
    mutationFn: async (data: FinalizeData) => {
      const token = await getAccessTokenSilently();
      return finalizeSnippet(snippet.id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["draftSnippets"] });
      onComplete();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deleteSnippet(snippet.id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["draftSnippets"] });
      onClose();
    },
  });

  // Update draft data helper
  const updateDraft = useCallback((updates: Partial<SnippetDraftData>) => {
    setDraftData((prev) => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  }, []);

  // Refresh image after edits
  const refreshImage = useCallback(() => {
    setImageKey((k) => k + 1);
  }, []);

  // Navigation
  const canGoNext = useCallback(() => {
    switch (currentStep) {
      case 1:
        // Image prep is always optional
        return true;
      case 2:
        // Classification requires snippet_type
        return !!draftData.snippet_type;
      case 3:
        // Metadata is optional
        return true;
      case 4:
        // Review - always can proceed
        return true;
      default:
        return false;
    }
  }, [currentStep, draftData.snippet_type]);

  const handleNext = useCallback(() => {
    if (currentStep < 4 && canGoNext()) {
      const newCompletedSteps = [...draftData.completed_steps];
      if (!newCompletedSteps.includes(currentStep)) {
        newCompletedSteps.push(currentStep);
      }
      updateDraft({ completed_steps: newCompletedSteps });
      setCurrentStep((currentStep + 1) as WizardStep);
    }
  }, [currentStep, canGoNext, draftData.completed_steps, updateDraft]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as WizardStep);
    }
  }, [currentStep]);

  const handleStepClick = useCallback(
    (step: WizardStep) => {
      // Allow clicking to any completed step or the current step + 1
      if (step <= currentStep || draftData.completed_steps.includes(step - 1)) {
        setCurrentStep(step);
      }
    },
    [currentStep, draftData.completed_steps]
  );

  // Submit / Finalize
  const handleSubmit = useCallback(async () => {
    if (!draftData.snippet_type) {
      return;
    }

    setIsSubmitting(true);
    try {
      const finalizeData: FinalizeData = {
        snippet_type: draftData.snippet_type,
        image_subtype: draftData.image_subtype || undefined,
        text_type: draftData.text_type || undefined,
        record_subtype: draftData.record_subtype || undefined,
        caption: draftData.caption || undefined,
        description: draftData.description || undefined,
        estimated_date_text: draftData.estimated_date_text || undefined,
        location: draftData.location || undefined,
        person_ids: draftData.person_ids || [],
        connected_person_ids: draftData.connected_person_ids || [],
        tree_id: treeId,
      };

      await finalizeMutation.mutateAsync(finalizeData);
    } finally {
      setIsSubmitting(false);
    }
  }, [draftData, treeId, finalizeMutation]);

  // Close / Discard
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges || draftData.current_step > 1) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, draftData.current_step, onClose]);

  const handleDiscard = useCallback(async () => {
    await deleteMutation.mutateAsync();
  }, [deleteMutation]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        if (currentStep === 4) {
          handleSubmit();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose, handleNext, handleSubmit, currentStep]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepImagePrep
            snippet={snippet}
            draftData={draftData}
            updateDraft={updateDraft}
            imageKey={imageKey}
            onImageEdited={refreshImage}
          />
        );
      case 2:
        return (
          <StepClassification
            snippet={snippet}
            draftData={draftData}
            updateDraft={updateDraft}
            imageKey={imageKey}
          />
        );
      case 3:
        return (
          <StepMetadata
            snippet={snippet}
            draftData={draftData}
            updateDraft={updateDraft}
            imageKey={imageKey}
          />
        );
      case 4:
        return (
          <StepReview
            snippet={snippet}
            draftData={draftData}
            imageKey={imageKey}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-vault-200 bg-vault-50">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-vault-900">Create Snippet</h2>
            {/* Auto-save indicator */}
            <span className="text-xs text-vault-500">
              {isSaving ? (
                <span className="flex items-center gap-1">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </span>
              ) : lastSaved ? (
                <span className="text-green-600">Saved</span>
              ) : saveError ? (
                <span className="text-red-600">Save failed</span>
              ) : null}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-vault-400 hover:text-vault-600 hover:bg-vault-100 rounded-lg transition-colors"
            aria-label="Close wizard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step indicators */}
        <div className="px-6 py-4 border-b border-vault-100 bg-white">
          <div className="flex items-center justify-between">
            {([1, 2, 3, 4] as WizardStep[]).map((step) => {
              const isActive = currentStep === step;
              const isCompleted = draftData.completed_steps.includes(step);
              const isClickable = step <= currentStep || draftData.completed_steps.includes(step - 1);

              return (
                <button
                  key={step}
                  onClick={() => handleStepClick(step)}
                  disabled={!isClickable}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? "bg-vault-600 text-white"
                      : isCompleted
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : isClickable
                      ? "bg-vault-100 text-vault-600 hover:bg-vault-200"
                      : "bg-vault-50 text-vault-400 cursor-not-allowed"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                      isActive
                        ? "bg-white/20"
                        : isCompleted
                        ? "bg-green-200"
                        : "bg-vault-200"
                    }`}
                  >
                    {isCompleted && !isActive ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      step
                    )}
                  </span>
                  <span className="text-sm font-medium hidden sm:inline">{STEP_TITLES[step]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto p-6">{renderStepContent()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-vault-200 bg-vault-50">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? "text-vault-400 cursor-not-allowed"
                : "text-vault-600 hover:bg-vault-100"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back
          </button>

          <div className="flex items-center gap-3">
            <span className="text-xs text-vault-500 hidden sm:inline">
              {currentStep === 4 ? "⌘+Enter to save" : "⌘+Enter for next"}
            </span>
            {currentStep === 4 ? (
              <button
                onClick={handleSubmit}
                disabled={!canGoNext() || isSubmitting || finalizeMutation.isPending}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                  canGoNext() && !isSubmitting
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-vault-200 text-vault-400 cursor-not-allowed"
                }`}
              >
                {isSubmitting || finalizeMutation.isPending ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Save to Library
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canGoNext()}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                  canGoNext()
                    ? "bg-vault-600 text-white hover:bg-vault-700"
                    : "bg-vault-200 text-vault-400 cursor-not-allowed"
                }`}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Discard confirmation modal */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-vault-900 mb-2">Discard snippet?</h3>
            <p className="text-vault-600 mb-4">
              This will delete the snippet and all your progress. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="px-4 py-2 text-vault-600 hover:bg-vault-100 rounded-lg font-medium transition-colors"
              >
                Keep editing
              </button>
              <button
                onClick={handleDiscard}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Discard"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {finalizeMutation.isError && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          <p className="font-medium">Failed to save snippet</p>
          <p className="text-sm">{(finalizeMutation.error as Error).message}</p>
        </div>
      )}
    </div>
  );
}

export default SnippetWizard;
